import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, formatApiErrorDetail } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, User, Building2, Trash2 } from 'lucide-react';
import { PARTIES } from '@/constants/testIds';
import { toast } from 'sonner';

export default function PartiesPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('CUSTOMER');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', document: '', email: '', phone: '', party_type: 'CUSTOMER', active: true });

  const { data } = useQuery({ queryKey: ['parties'], queryFn: async () => (await api.get('/parties')).data });
  const items = (data || []).filter((p) => p.party_type === tab && p.active !== false);

  const saveMut = useMutation({
    mutationFn: async (payload) => (await api.post('/parties', payload)).data,
    onSuccess: () => { toast.success('Salvo'); setOpen(false); qc.invalidateQueries({ queryKey: ['parties'] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/parties/${id}`)).data,
    onSuccess: () => { toast.success('Removido'); qc.invalidateQueries({ queryKey: ['parties'] }); },
  });

  return (
    <div data-testid={PARTIES.page} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Cadastro</div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Clientes & Fornecedores</h1>
        </div>
        <Button data-testid={PARTIES.newBtn} onClick={() => { setForm({ name: '', document: '', email: '', phone: '', party_type: tab, active: true }); setOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Plus className="h-4 w-4 mr-2" /> Novo</Button>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="CUSTOMER">Clientes</TabsTrigger>
          <TabsTrigger value="SUPPLIER">Fornecedores</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((p) => (
          <Card key={p.id} className="p-4 flex items-start gap-3 card-elevate border-border/70">
            <div className={`h-10 w-10 rounded-lg grid place-items-center ${p.party_type === 'CUSTOMER' ? 'bg-blue-500/10 text-blue-500' : 'bg-amber-500/10 text-amber-500'}`}>
              {p.party_type === 'CUSTOMER' ? <User className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{p.name}</div>
              {p.document && <div className="text-xs text-muted-foreground font-mono">{p.document}</div>}
              {p.email && <div className="text-xs text-muted-foreground truncate">{p.email}</div>}
            </div>
            <Button size="icon" variant="ghost" className="text-rose-500" onClick={() => { if (confirm('Remover?')) delMut.mutate(p.id); }}><Trash2 className="h-4 w-4" /></Button>
          </Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo {form.party_type === 'CUSTOMER' ? 'cliente' : 'fornecedor'}</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); saveMut.mutate(form); }} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2"><Label>Nome</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>CPF/CNPJ</Label><Input value={form.document} onChange={(e) => setForm({ ...form, document: e.target.value })} /></div>
              <div className="space-y-2"><Label>Telefone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="col-span-2 space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="col-span-2 space-y-2">
                <Label>Tipo</Label>
                <Select value={form.party_type} onValueChange={(v) => setForm({ ...form, party_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="CUSTOMER">Cliente</SelectItem><SelectItem value="SUPPLIER">Fornecedor</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
